import { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import {
    Backdrop,
    Box,
    Typography,
    Stack,
    CircularProgress,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Skeleton,
} from '@mui/material';

import { formatToLongDate } from '../../utils/formatToLongDate';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';

import CustomSearch from '../custom_components/CustomSearch';
import CustomButton from '../custom_components/CustomButton';
import CustomModalHeader from '../custom_components/CustomModalHeader';

import SearchNotFound from '../../utils/SearchNotFound';

const apiUrl = process.env.REACT_APP_API_URL;

export default function AddToListSelection(props) {

    const { open, onClose, selectedRows, onUpdate } = props

    const [selectedList, setSelectedList] = useState('');
    const [lists, setLists] = useState([]);

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState(search);

    const [loadingDisplay, setLoadingDisplay] = useState(false);
    const [loadingAddToList, setLoadingAddToList] = useState(false);

    // DISPLAY LIST'S
    const handleDisplayList = async (searchValue = '') => {
        setLoadingDisplay(true);
        setLists([]);

        try {
            const response = await fetch(`${apiUrl}manage_orders/get_list_names.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    search: searchValue,
                })
            });

            const result = await response.json();

            if (result.status === 'success') {

                setLists(result.data || []);

            } else if (result.status === 'error') {
                Swal.fire({
                    title: 'Server Error!',
                    text: result.message, icon: 'error'
                });
            }
        } catch (error) {
            Swal.fire({
                title: 'Server Error!',
                text: error, icon: 'error'
            });
            console.error('Server error:', error);
        } finally {
            setLoadingDisplay(false);
        }
    };

    useEffect(() => {
        setSelectedList('');
        handleDisplayList(debouncedSearch);
    }, [open, debouncedSearch]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500); // 500ms delay

        return () => clearTimeout(timer);
    }, [search]);

    const selectedIDRows = useMemo(() => {
        return selectedRows.map(row => ({
            CustomerID: row.CustomerID,
            CustomerName: row.CustomerName,
            ItemName: row.ItemName,
            OrderID: row.OrderID,
        }));
    }, [selectedRows]);

    // REMOVE DUPLICATE ORDER ID BEFORE ADD TO LIST
    const removeDuplicateOrderIDs = (orders) => {
        const seen = new Set();

        return orders.filter(order => {
            if (seen.has(order.OrderID)) {
                return false; // remove duplicate
            }

            seen.add(order.OrderID);
            return true; // keep first occurrence
        });
    };

    const handleAddToListAlert = (result) => {
        Swal.fire({
            title: "Order Summary",
            html: `
               <div style="display:flex; gap:10px; font-size:14px; margin-bottom:10px;">
                    <div style="flex:1; background:#e8f5e9; padding:6px; border-radius:6px;">
                        <b>${result.inserted}</b><br/>Added to list
                    </div>
                    <div style="flex:1; background:#fff8e1; padding:6px; border-radius:6px;">
                        <b>${result.skipped}</b><br/>In a list
                    </div>
                    <div style="flex:1; background:#ffebee; padding:6px; border-radius:6px;">
                        <b>${result.blocked}</b><br/>In other list
                    </div>
                </div>

            ${result.details?.length
                    ? `
                <hr/>
                <div style="text-align:left; font-size:13px; margin-top:10px; max-height:260px; overflow:auto;">
                    <b>Already in a other list:</b>
                    <table style="width:100%; margin-top:8px; font-size:12px;">
                        <thead>
                            <tr>
                                <th align="left">Customer</th>
                                <th align="left">Order</th>
                                <th align="left">List</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${result.details
                        .map(
                            (item) => `
                                    <tr>
                                        <td>${item.CustomerID}</td>
                                        <td>${item.OrderID}</td>
                                        <td>${item.ExistingListID}</td>
                                    </tr>
                                `
                        )
                        .join("")}
                        </tbody>
                    </table>
                </div>
                `
                    : ""
                }
                `,
            icon: "info",
            confirmButtonText: "OK",
            width: 600,
        });
    }

    // ADD TO LIST ORDERS API
    const handleAddToList = async () => {

        setLoadingAddToList(true);

        const validatedIds = removeDuplicateOrderIDs(selectedIDRows);

        try {
            const response = await fetch(
                `${apiUrl}manage_orders/add_to_list.php`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        listId: selectedList,
                        selectedOrders: validatedIds,
                    })
                });

            const result = await response.json();

            if (result.status === 'success') {

                handleAddToListAlert(result);

                onUpdate(result.added || []);

                handleClose();

            } else if (result.status === 'error') {
                Swal.fire({
                    title: 'Server Error!',
                    text: result.message, icon: 'error'
                });
            }

        } catch (error) {
            Swal.fire({
                title: 'Server Error!',
                text: error, icon: 'error'
            });
            console.error('Server error:', error);
        } finally {
            setLoadingAddToList(false);
        }
    };

    const handleClose = () => {
        setSelectedList("");
        setSearch("");
        onClose();
    };

    // LOADING SKELETON
    const ListSkeleton = ({ rows = 6 }) => {
        return (
            <List disablePadding>
                {Array.from({ length: rows }).map((_, index) => (
                    <ListItem
                        key={index}
                        disablePadding
                        sx={{ mb: 0.5 }}
                    >
                        <ListItemButton
                            disabled
                            sx={{
                                borderRadius: 1,
                                border: "1px solid #E5E7EB",
                                position: "relative",
                                overflow: "hidden",
                                py: 1.2,
                                px: 2,
                            }}
                        >
                            <Box sx={{ flexGrow: 1 }}>
                                <Skeleton
                                    variant="text"
                                    width="55%"
                                    height={24}
                                />

                                <Skeleton
                                    variant="text"
                                    width="35%"
                                    height={16}
                                />
                            </Box>

                            <Skeleton
                                variant="circular"
                                width={22}
                                height={22}
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        );
    };

    return (
        <Backdrop
            aria-hidden="false"
            open={open}
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                color: '#fff',
                backdropFilter: 'blur(1px)'
            }}
        >
            <Box
                onClick={(e) => e.stopPropagation()}
                sx={{
                    backgroundColor: '#fff',
                    p: 3,
                    borderRadius: 2,
                    width: '100%',
                    maxWidth: 500,

                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "4px",
                        background: "#2e3489"
                    }
                }}
            >

                <CustomModalHeader
                    title="Add to List"
                    subtitle="Select a list to add the selected order(s)."
                    onClose={onClose}
                />

                {/* DISPLAY LIST UI */}
                <Stack direction='column' gap={2} mt={2}>
                    <Stack direction='row' gap={2}>
                        <CustomSearch
                            md="35ch"
                            placeholder="Search a list"
                            value={search}
                            onChange={(val) => setSearch(val)}
                        />
                        <IconButton
                            aria-label="refresh"
                            size="small"
                            disabled={loadingDisplay}
                            onClick={() => handleDisplayList(debouncedSearch)}
                            sx={{
                                width: 36,
                                height: 36,

                                backgroundColor: "#1976d2",
                                color: "#ffffff",

                                borderRadius: "8px",

                                transition: "all .2s ease",

                                "&:hover": {
                                    backgroundColor: "#3B82F6",
                                    borderColor: "#3B82F6",
                                },

                                '&:disabled': {
                                    backgroundColor: '#e0e0e0', // soft gray
                                    color: '#9e9e9e',           // muted text
                                    boxShadow: 'none',
                                }
                            }}
                        >
                            {loadingDisplay ? (
                                <CircularProgress
                                    size={18}
                                    thickness={5}
                                    color="inherit"
                                />
                            ) : (
                                <RefreshIcon fontSize="small" />
                            )}
                        </IconButton>
                    </Stack>

                    <Stack
                        sx={{ maxHeight: '56vh', minHeight: 260, overflowY: 'auto', pb: 2, pt: 2 }}
                        spacing={1}
                    >
                        {loadingDisplay ? (

                            <ListSkeleton rows={3} />

                        ) : lists.length === 0 ? (

                            <SearchNotFound />

                        ) : (
                            <List disablePadding>
                                {lists.map((list) => {
                                    const isSelected =
                                        selectedList === list.ListID;

                                    return (
                                        <ListItem
                                            key={list.ListID}
                                            disablePadding
                                            sx={{ mb: 0.5 }}
                                        >
                                            <ListItemButton
                                                onClick={() =>
                                                    setSelectedList(list.ListID)
                                                }
                                                sx={{
                                                    borderRadius: 1,

                                                    position: "relative",
                                                    overflow: "hidden",
                                                    transition: "all .2s ease",

                                                    border: "1px solid",
                                                    borderColor: isSelected
                                                        ? "#A5D6A7"
                                                        : "#E5E7EB",

                                                    backgroundColor: isSelected
                                                        ? "#F0FDF4"
                                                        : "#FFFFFF",

                                                    ...(isSelected && {
                                                        boxShadow:
                                                            "0 4px 12px rgba(46,125,50,.12)",

                                                        "&::before": {
                                                            content: '""',
                                                            position: "absolute",
                                                            left: 0,
                                                            top: 0,
                                                            width: "4px",
                                                            height: "100%",
                                                            backgroundColor: "#2E7D32",
                                                        },
                                                    }),

                                                    "&:hover": {
                                                        borderColor: isSelected
                                                            ? "#81C784"
                                                            : "#90CAF9",
                                                    },

                                                    ...(!isSelected && {
                                                        "&:hover::before": {
                                                            content: '""',
                                                            position: "absolute",
                                                            left: 0,
                                                            top: 0,
                                                            width: "4px",
                                                            height: "100%",
                                                            backgroundColor: "#1976D2",
                                                        },
                                                    }),
                                                }}
                                            >
                                                <ListItemText
                                                    primary={list.ListName}
                                                    secondary={
                                                        <Typography
                                                            sx={{
                                                                fontSize: 12,
                                                                fontWeight: 500,
                                                                color: '#94A3B8'
                                                            }}
                                                        >
                                                            {formatToLongDate(list.DateCreated)}
                                                        </Typography>
                                                    }
                                                    primaryTypographyProps={{
                                                        color: '#000000',
                                                        fontWeight: isSelected ? 600 : 500,
                                                    }}
                                                />

                                                {isSelected && (
                                                    <CheckCircleIcon
                                                        sx={{
                                                            color: "#2E7D32",
                                                            fontSize: 22,
                                                            ml: 1,
                                                        }}
                                                    />
                                                )}
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        )}
                    </Stack>
                </Stack>

                {/* ACTIONS BUTTONS */}
                <Stack direction="row" justifyContent="right" sx={{ mt: 3 }} spacing={2}>
                    <CustomButton
                        label="Cancel"
                        variant="outlined"
                        hoverColor="#d14249"
                        onClick={handleClose}
                    />

                    <CustomButton
                        label="Add to List"
                        disabled={
                            loadingAddToList ||
                            selectedList === null ||
                            selectedList === ""
                        }
                        loading={loadingAddToList}
                        onClick={handleAddToList}
                    />
                </Stack>
            </Box>
        </Backdrop>
    );
}
